// The About page carries the place-data credits (ADR 0229, ADR 0327).
// Native source identities also retain their individual attribution.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useDocumentTitle } from '../lib/useDocumentTitle';

function CreditLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className="link" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

export function About() {
  useDocumentTitle('Neomaris | About');

  return (
    <section className="legal-prose">
      <h2>About Neomaris</h2>
      <p>
        Neomaris is intelligent monitoring for every vessel, from dock to open water. Neomaris Core
        runs aboard the boat and reads its instruments. Neomaris Cloud keeps the boat&apos;s history
        and brings its dashboard, alarms and Neo, the boat&apos;s assistant, to the web.
      </p>

      <h3>Data credits</h3>
      <p>
        Place names from{' '}
        <a className="link" href="https://www.geonames.org/" target="_blank" rel="noreferrer">
          GeoNames
        </a>
        , licensed under{' '}
        <a
          className="link"
          href="https://creativecommons.org/licenses/by/4.0/"
          target="_blank"
          rel="noreferrer"
        >
          CC BY 4.0
        </a>
        . Neomaris uses the GeoNames towns within 25 km of a port, with their region and country
        names, to name the places a boat has been. Our place catalogue also selects coastal features
        from GeoNames country datasets and combines matching source records.
      </p>
      <p>
        OpenStreetMap and OpenSeaMap place and seamark data: ©{' '}
        <CreditLink href="https://www.openstreetmap.org/copyright">
          OpenStreetMap contributors
        </CreditLink>
        , available under the{' '}
        <CreditLink href="https://opendatacommons.org/licenses/odbl/1-0/">
          Open Database License (ODbL) 1.0
        </CreditLink>
        .
      </p>
      <p>
        Navigation aids on Neomaris&apos;s maps (buoys, beacons and lights): ©{' '}
        <CreditLink href="https://www.openstreetmap.org/copyright">
          OpenStreetMap contributors
        </CreditLink>{' '}
        (<CreditLink href="https://opendatacommons.org/licenses/odbl/1-0/">ODbL</CreditLink>), built
        by Neomaris from regional OpenStreetMap extracts. The map itself is © MapTiler ©
        OpenStreetMap contributors. Background context only, never for navigation.
      </p>
      <p>
        Marina records from{' '}
        <CreditLink href="https://docs.overturemaps.org/attribution/#places">
          Overture Maps Foundation and its data providers
        </CreditLink>
        . The selected Overture Places records used by Neomaris are available under{' '}
        <CreditLink href="https://cdla.dev/permissive-2-0/">CDLA-Permissive 2.0</CreditLink>.
      </p>
      <p>
        Contains information licensed under the{' '}
        <CreditLink href="https://open.canada.ca/en/open-government-licence-canada">
          Open Government Licence – Canada
        </CreditLink>
        : Natural Resources Canada&apos;s{' '}
        <CreditLink href="https://natural-resources.canada.ca/maps-tools-publications/tools-applications/canadian-geographical-names-database">
          Canadian Geographical Names Database
        </CreditLink>
        , with names supplied by federal, provincial and territorial naming authorities, and
        Statistics Canada&apos;s{' '}
        <CreditLink href="https://www150.statcan.gc.ca/n1/pub/34-26-0003/342600032023001-eng.htm">
          Open Database of Infrastructure
        </CreditLink>{' '}
        and{' '}
        <CreditLink href="https://www.statcan.gc.ca/en/lode/databases/odrsf">
          Open Database of Recreational and Sport Facilities
        </CreditLink>
        . Facility-data contributors used here include Niagara Region, Huron County, Gatineau,
        Brampton, Saint John, Grey County, Kelowna, Oakville, Hamilton, Surrey, Québec City and
        Rimouski. Their{' '}
        <CreditLink href="https://www150.statcan.gc.ca/n1/en/pub/21-26-0002/2021001/ODRSF_v1.0.zip">
          provider credits and terms
        </CreditLink>{' '}
        accompany the source datasets, including{' '}
        <CreditLink href="https://www.donneesquebec.ca/fr/licence/#cc-by">
          Québec&apos;s CC BY terms
        </CreditLink>
        .
      </p>
      <p>
        U.S. government public data from{' '}
        <CreditLink href="https://www.nauticalcharts.noaa.gov/data/data-licensing.html">
          NOAA Electronic Navigational Charts
        </CreditLink>
        ,{' '}
        <CreditLink href="https://www.usgs.gov/us-board-on-geographic-names/domestic-names">
          USGS Geographic Names Information System
        </CreditLink>
        , NGA&apos;s{' '}
        <CreditLink href="https://msi.nga.mil/Publications/WPI">World Port Index</CreditLink> and{' '}
        <CreditLink href="https://geonames.nga.mil/geonames/GNSHome/welcome.html">
          Geographic Names Server
        </CreditLink>
        , and the{' '}
        <CreditLink href="https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html">
          U.S. Census Bureau&apos;s TIGER/Line
        </CreditLink>{' '}
        datasets.
      </p>
      <p>
        Structured place names and identifiers from{' '}
        <CreditLink href="https://www.wikidata.org/wiki/Wikidata:Licensing">Wikidata</CreditLink>{' '}
        are available under{' '}
        <CreditLink href="https://creativecommons.org/publicdomain/zero/1.0/">CC0</CreditLink>.
      </p>

      <p className="legal-back">
        <Link className="link" to="/">
          &larr; Back to home
        </Link>
      </p>
    </section>
  );
}
